import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Eye, EyeOff, Trash2 } from 'lucide-react';
import CanvasElement from './CanvasElement';
import {
  CanvasElement as CanvasElementType,
  CanvasConfig,
  CanvasLayoutData
} from '@/types/certificate-template.types';

interface CertificateCanvasProps {
  layoutData: CanvasLayoutData;
  selectedElementId: string | null;
  sampleData?: Record<string, string>;
  backgroundHtml?: string | null;
  onSelectElement: (id: string | null) => void;
  onUpdateElement: (id: string, updates: Partial<CanvasElementType>) => void;
  onDeleteElement: (id: string) => void;
  onDuplicateElement: (id: string) => void;
  onUpdateCanvas: (config: Partial<CanvasConfig>) => void;
  onDropElement?: (elementData: any, position: { x: number; y: number }) => void;
}

type BorderSelection = 'outer' | 'inner' | 'corners' | null;
type EdgeSide = 'top' | 'left' | 'right' | 'bottom';
type DragSide = EdgeSide | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export default function CertificateCanvas({
  layoutData,
  selectedElementId,
  sampleData = {},
  backgroundHtml,
  onSelectElement,
  onUpdateElement,
  onDeleteElement,
  onDuplicateElement,
  onUpdateCanvas,
  onDropElement
}: CertificateCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.8);
  const [showGrid] = useState(layoutData.gridEnabled ?? true);
  const [dragOver, setDragOver] = useState(false);
  const [selectedBorder, setSelectedBorder] = useState<BorderSelection>(null);
  const [draggingEdge, setDraggingEdge] = useState<{ border: 'outer' | 'inner' | 'corners'; side: DragSide } | null>(null);
  const [edgeDragStart, setEdgeDragStart] = useState({ clientX: 0, clientY: 0, initialInsetX: 0, initialInsetY: 0 });

  const { canvas, elements } = layoutData;

  // Handle zoom
  const handleZoom = useCallback((delta: number) => {
    setScale(prev => Math.min(2, Math.max(0.25, prev + delta)));
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementId) {
          e.preventDefault();
          onDeleteElement(selectedElementId);
        }
      }

      if (e.key === 'Escape') {
        onSelectElement(null);
        setSelectedBorder(null);
      }

      // Zoom shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          handleZoom(0.1);
        } else if (e.key === '-') {
          e.preventDefault();
          handleZoom(-0.1);
        } else if (e.key === '0') {
          e.preventDefault();
          setScale(1);
        }
      }

      // Arrow keys for moving elements
      if (selectedElementId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const element = elements.find(el => el.id === selectedElementId);
        if (element && !element.locked) {
          const step = e.shiftKey ? 10 : 1;
          let dx = 0, dy = 0;
          if (e.key === 'ArrowUp') dy = -step;
          if (e.key === 'ArrowDown') dy = step;
          if (e.key === 'ArrowLeft') dx = -step;
          if (e.key === 'ArrowRight') dx = step;

          onUpdateElement(selectedElementId, {
            position: {
              ...element.position,
              x: Math.max(0, element.position.x + dx),
              y: Math.max(0, element.position.y + dy)
            }
          });
        }
      }

      // Duplicate shortcut (Ctrl/Cmd + D)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElementId) {
        e.preventDefault();
        onDuplicateElement(selectedElementId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, elements, handleZoom, onDeleteElement, onSelectElement, onUpdateElement, onDuplicateElement]);

  // Handle drag and drop from elements panel
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (!canvasRef.current || !onDropElement) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    // Save scroll position before state update — re-render can reset it
    const scrollContainer = containerRef.current;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      onDropElement(data, { x, y });

      // Restore scroll position after React re-render
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
          scrollContainer.scrollLeft = scrollLeft;
        }
      });
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [scale, onDropElement]);

  // Handle canvas click (deselect both elements and borders)
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      onSelectElement(null);
      setSelectedBorder(null);
    }
  }, [onSelectElement]);

  // Select a decorative border on click
  const handleBorderClick = useCallback((e: React.MouseEvent, border: 'outer' | 'inner' | 'corners') => {
    e.stopPropagation();
    onSelectElement(null);
    setSelectedBorder(border);
  }, [onSelectElement]);

  // Start dragging a border edge or corner handle
  const handleEdgeDragStart = useCallback((e: React.MouseEvent, border: 'outer' | 'inner' | 'corners', side: DragSide) => {
    e.stopPropagation();
    e.preventDefault();
    const db = canvas.decorativeBorder;
    const borderConfig = db?.[border];
    if (!borderConfig) return;

    // For corner drags, capture both axes; for single edge, store in Y for vertical, X for horizontal
    const isCorner = side.includes('-');
    const verticalSide: EdgeSide = side.includes('top') ? 'top' : 'bottom';
    const horizontalSide: EdgeSide = side.includes('left') ? 'left' : 'right';
    setDraggingEdge({ border, side });
    setEdgeDragStart({
      clientX: e.clientX,
      clientY: e.clientY,
      initialInsetX: isCorner ? borderConfig.inset[horizontalSide] : borderConfig.inset[side as EdgeSide],
      initialInsetY: isCorner ? borderConfig.inset[verticalSide] : borderConfig.inset[side as EdgeSide]
    });
  }, [canvas.decorativeBorder]);

  // Handle edge dragging — update inset in real-time (supports single edge + corner dual-axis)
  useEffect(() => {
    if (!draggingEdge) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { border, side } = draggingEdge;
      const db = canvas.decorativeBorder;
      if (!db?.[border]) return;

      const deltaX = (e.clientX - edgeDragStart.clientX) / scale;
      const deltaY = (e.clientY - edgeDragStart.clientY) / scale;
      const currentInset = { ...db[border]!.inset };

      if (side.includes('-')) {
        // Corner drag — update two insets at once
        const isTop = side.includes('top');
        const isLeft = side.includes('left');
        currentInset[isTop ? 'top' : 'bottom'] = Math.max(0, Math.round(edgeDragStart.initialInsetY + (isTop ? deltaY : -deltaY)));
        currentInset[isLeft ? 'left' : 'right'] = Math.max(0, Math.round(edgeDragStart.initialInsetX + (isLeft ? deltaX : -deltaX)));
      } else {
        // Single edge drag
        let delta = 0;
        if (side === 'top') delta = deltaY;
        else if (side === 'bottom') delta = -deltaY;
        else if (side === 'left') delta = deltaX;
        else if (side === 'right') delta = -deltaX;
        currentInset[side as EdgeSide] = Math.max(0, Math.round(edgeDragStart.initialInsetY + delta));
      }

      onUpdateCanvas({
        decorativeBorder: {
          ...db,
          [border]: { ...db[border]!, inset: currentInset }
        }
      });
    };

    const handleMouseUp = () => setDraggingEdge(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingEdge, edgeDragStart, scale, canvas.decorativeBorder, onUpdateCanvas]);

  // Deselect border when an element is selected
  useEffect(() => {
    if (selectedElementId) setSelectedBorder(null);
  }, [selectedElementId]);

  // Render grid
  const renderGrid = () => {
    if (!showGrid) return null;
    const gridSize = layoutData.gridSize || 20;

    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        width="100%"
        height="100%"
        style={{ opacity: 0.3 }}
      >
        <defs>
          <pattern
            id="grid"
            width={gridSize}
            height={gridSize}
            patternUnits="userSpaceOnUse"
          >
            <path
              d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
              fill="none"
              stroke="#9CA3AF"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    );
  };

  // Render background
  const renderBackground = () => {
    const { background } = canvas;

    if (background.type === 'solid') {
      return { backgroundColor: background.color || '#FFFFFF' };
    }

    if (background.type === 'gradient' && background.gradient) {
      const { gradient } = background;
      const colorStops = gradient.colors
        .map(c => `${c.color} ${c.position}%`)
        .join(', ');

      if (gradient.type === 'linear') {
        return {
          background: `linear-gradient(${gradient.angle || 0}deg, ${colorStops})`
        };
      } else {
        return {
          background: `radial-gradient(circle, ${colorStops})`
        };
      }
    }

    if (background.type === 'image' && background.image?.src) {
      return {
        backgroundImage: `url(${background.image.src})`,
        backgroundSize: background.image.fit === 'tile' ? 'auto' : background.image.fit,
        backgroundRepeat: background.image.fit === 'tile' ? 'repeat' : 'no-repeat',
        backgroundPosition: 'center',
        opacity: background.image.opacity ?? 1
      };
    }

    return { backgroundColor: '#FFFFFF' };
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-800 overflow-hidden">
      {/* Canvas Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-center px-4 py-2 bg-gray-700 border-b border-gray-600">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleZoom(-0.1)}
            className="p-2 text-gray-400 hover:text-white rounded transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-white min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => handleZoom(0.1)}
            className="p-2 text-gray-400 hover:text-white rounded transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto p-10 flex items-center justify-center"
        style={{
          backgroundImage: 'radial-gradient(circle, #4B5563 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      >
        {/* Wrapper to handle scaled dimensions for correct scrollbars */}
        <div
          className="relative flex-shrink-0"
          style={{ width: canvas.width * scale, height: canvas.height * scale }}
        >
          {/* Canvas */}
          <div
            ref={canvasRef}
            className={`absolute top-0 left-0 w-full h-full shadow-2xl transition-shadow ${dragOver ? 'ring-4 ring-brand-blue-500 ring-opacity-50' : ''}`}
            style={{
              width: canvas.width,
              height: canvas.height,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              ...renderBackground(),
              border: canvas.border
                ? `${canvas.border.width}px ${canvas.border.style} ${canvas.border.color}`
                : undefined,
              borderRadius: canvas.border?.radius ? `${canvas.border.radius}px` : undefined
            }}
            onClick={handleCanvasClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
          {/* Background: Full HTML template design (decorative elements, borders, layout) */}
          {backgroundHtml && (
            <iframe
              srcDoc={backgroundHtml}
              title="Template Background"
              sandbox="allow-same-origin"
              className="absolute inset-0 w-full h-full border-none"
              style={{ pointerEvents: 'none', zIndex: 0 }}
            />
          )}

          {/* Decorative borders — clickable + drag handles rendered at canvas level.
              Border wrappers stay at z-index 1 so elements (z-index 1+ with later DOM order)
              are always on top and clickable. Drag handles are rendered as separate canvas-level
              siblings at z-index 50 so they remain accessible above elements for edge dragging. */}
          {canvas.decorativeBorder?.outer?.enabled && (() => {
            const o = canvas.decorativeBorder!.outer!;
            const isSelected = selectedBorder === 'outer';
            return (
              <>
                {/* Wrapper: visual border + click target. z-index 1 always — never raised on selection
                    to avoid blocking elements. Elements are rendered later in DOM, so they stack above. */}
                <div
                  className="absolute"
                  style={{
                    top: o.inset.top - 4, left: o.inset.left - 4, right: o.inset.right - 4, bottom: o.inset.bottom - 4,
                    padding: 4,
                    zIndex: 1, cursor: 'pointer'
                  }}
                  onClick={(e) => handleBorderClick(e, 'outer')}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      border: `${o.width}px ${o.style} ${o.color}`,
                      outline: isSelected ? '2px dashed #3B82F6' : undefined,
                      outlineOffset: 2,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                {/* Drag handles at canvas level (z-index 50) — above elements for easy dragging */}
                {isSelected && (
                  <>
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ top: o.inset.top - 7, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag top edge" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'top')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ bottom: o.inset.bottom - 7, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag bottom edge" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'bottom')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ left: o.inset.left - 7, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag left edge" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ right: o.inset.right - 7, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag right edge" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ top: o.inset.top - 7, left: o.inset.left - 7, zIndex: 50 }} title="Drag top-left corner" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'top-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ top: o.inset.top - 7, right: o.inset.right - 7, zIndex: 50 }} title="Drag top-right corner" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'top-right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ bottom: o.inset.bottom - 7, left: o.inset.left - 7, zIndex: 50 }} title="Drag bottom-left corner" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'bottom-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ bottom: o.inset.bottom - 7, right: o.inset.right - 7, zIndex: 50 }} title="Drag bottom-right corner" onMouseDown={(e) => handleEdgeDragStart(e, 'outer', 'bottom-right')} />
                  </>
                )}
              </>
            );
          })()}
          {canvas.decorativeBorder?.inner?.enabled && (() => {
            const n = canvas.decorativeBorder!.inner!;
            const isSelected = selectedBorder === 'inner';
            return (
              <>
                <div
                  className="absolute"
                  style={{
                    top: n.inset.top - 4, left: n.inset.left - 4, right: n.inset.right - 4, bottom: n.inset.bottom - 4,
                    padding: 4,
                    zIndex: 1, cursor: 'pointer'
                  }}
                  onClick={(e) => handleBorderClick(e, 'inner')}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      border: `${n.width}px ${n.style} ${n.color}`,
                      outline: isSelected ? '2px dashed #3B82F6' : undefined,
                      outlineOffset: 2,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                {isSelected && (
                  <>
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ top: n.inset.top - 7, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag top edge" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'top')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ bottom: n.inset.bottom - 7, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag bottom edge" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'bottom')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ left: n.inset.left - 7, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag left edge" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ right: n.inset.right - 7, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag right edge" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ top: n.inset.top - 7, left: n.inset.left - 7, zIndex: 50 }} title="Drag top-left corner" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'top-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ top: n.inset.top - 7, right: n.inset.right - 7, zIndex: 50 }} title="Drag top-right corner" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'top-right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ bottom: n.inset.bottom - 7, left: n.inset.left - 7, zIndex: 50 }} title="Drag bottom-left corner" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'bottom-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ bottom: n.inset.bottom - 7, right: n.inset.right - 7, zIndex: 50 }} title="Drag bottom-right corner" onMouseDown={(e) => handleEdgeDragStart(e, 'inner', 'bottom-right')} />
                  </>
                )}
              </>
            );
          })()}
          {canvas.decorativeBorder?.corners?.enabled && (() => {
            const c = canvas.decorativeBorder!.corners!;
            const isSelected = selectedBorder === 'corners';
            const positions = [
              { top: c.inset.top, left: c.inset.left, borderWidth: `${c.width}px 0 0 ${c.width}px` },
              { top: c.inset.top, right: c.inset.right, borderWidth: `${c.width}px ${c.width}px 0 0` },
              { bottom: c.inset.bottom, left: c.inset.left, borderWidth: `0 0 ${c.width}px ${c.width}px` },
              { bottom: c.inset.bottom, right: c.inset.right, borderWidth: `0 ${c.width}px ${c.width}px 0` }
            ];
            return (
              <>
                {positions.map((pos, i) => (
                  <div
                    key={`corner-${i}`}
                    className="absolute"
                    style={{
                      ...pos,
                      width: c.size, height: c.size,
                      borderColor: c.color, borderStyle: 'solid',
                      zIndex: 1, cursor: 'pointer',
                      outline: isSelected ? '1px dashed #3B82F6' : undefined
                    } as React.CSSProperties}
                    onClick={(e) => handleBorderClick(e, 'corners')}
                  />
                ))}
                {isSelected && (
                  <>
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ top: c.inset.top - 6, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag corners top" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'top')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ns-resize" style={{ bottom: c.inset.bottom - 6, left: '50%', transform: 'translateX(-50%)', zIndex: 50 }} title="Drag corners bottom" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'bottom')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ left: c.inset.left - 6, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag corners left" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-ew-resize" style={{ right: c.inset.right - 6, top: '50%', transform: 'translateY(-50%)', zIndex: 50 }} title="Drag corners right" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ top: c.inset.top - 6, left: c.inset.left - 6, zIndex: 50 }} title="Drag top-left" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'top-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ top: c.inset.top - 6, right: c.inset.right - 6, zIndex: 50 }} title="Drag top-right" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'top-right')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nesw-resize" style={{ bottom: c.inset.bottom - 6, left: c.inset.left - 6, zIndex: 50 }} title="Drag bottom-left" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'bottom-left')} />
                    <div className="absolute w-3 h-3 bg-brand-blue-500 rounded-sm cursor-nwse-resize" style={{ bottom: c.inset.bottom - 6, right: c.inset.right - 6, zIndex: 50 }} title="Drag bottom-right" onMouseDown={(e) => handleEdgeDragStart(e, 'corners', 'bottom-right')} />
                  </>
                )}
              </>
            );
          })()}

          {/* Border action toolbar — eye (toggle visibility) and delete */}
          {selectedBorder && canvas.decorativeBorder?.[selectedBorder] && (
            <div
              className="absolute flex items-center gap-1 bg-white rounded-lg shadow-lg border px-1 py-0.5"
              style={{
                top: -36,
                left: selectedBorder === 'corners' ? canvas.decorativeBorder.corners!.inset.left : canvas.decorativeBorder[selectedBorder]!.inset.left,
                zIndex: 60
              }}
            >
              <span className="text-xs text-gray-500 px-1 font-medium capitalize">{selectedBorder}</span>
              <div className="w-px h-4 bg-gray-200" />
              <button
                onClick={() => {
                  onUpdateCanvas({
                    decorativeBorder: {
                      ...canvas.decorativeBorder,
                      [selectedBorder]: {
                        ...canvas.decorativeBorder![selectedBorder]!,
                        enabled: !canvas.decorativeBorder![selectedBorder]!.enabled
                      }
                    }
                  });
                }}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title={canvas.decorativeBorder[selectedBorder]!.enabled ? 'Hide' : 'Show'}
              >
                {canvas.decorativeBorder[selectedBorder]!.enabled ? (
                  <Eye className="w-3.5 h-3.5 text-gray-500" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5 text-gray-400" />
                )}
              </button>
              <button
                onClick={() => {
                  onUpdateCanvas({
                    decorativeBorder: {
                      ...canvas.decorativeBorder,
                      [selectedBorder]: {
                        ...canvas.decorativeBorder![selectedBorder]!,
                        enabled: false
                      }
                    }
                  });
                  setSelectedBorder(null);
                }}
                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                title="Remove border"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </button>
            </div>
          )}

          {/* Grid overlay */}
          {renderGrid()}

          {/* Elements */}
          {elements.map((element) => (
            <CanvasElement
              key={element.id}
              element={element}
              isSelected={selectedElementId === element.id}
              scale={scale}
              sampleData={sampleData}
              onSelect={onSelectElement}
              onUpdate={onUpdateElement}
              onDelete={onDeleteElement}
              onDuplicate={onDuplicateElement}
            />
          ))}

          {/* Drop indicator */}
          {dragOver && (
            <div className="absolute inset-0 bg-brand-blue-500 bg-opacity-10 pointer-events-none flex items-center justify-center">
              <div className="bg-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-brand-blue-600">
                Drop element here
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
